import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  RefreshCw, Check, X, Pencil, Rss, Radio, Loader2, Inbox,
  Plus, Trash2, ExternalLink, AlertCircle,
} from "lucide-react";

const CATEGORY_KEYS = ["narmadapuram", "mp", "national", "politics", "rajkaj", "edujobs", "utility", "sportsent", "religion", "exclusive"];

export default function FeedInbox() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("pending");
  const [edit, setEdit] = useState(null);
  const [pulling, setPulling] = useState(false);
  const [pullingFeed, setPullingFeed] = useState(null);
  const [cats, setCats] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", url: "", type: "routine", mode: "review", category: "national" });
  const [addErr, setAddErr] = useState("");
  const [addBusy, setAddBusy] = useState(false);
  const [pullResult, setPullResult] = useState("");

  const load = useCallback(async () => {
    const [it, fs, st] = await Promise.all([
      api.get(`/feed-items?status=${tab}`),
      api.get("/feeds"),
      api.get("/feed-stats"),
    ]);
    setItems(it.data); setFeeds(fs.data); setStats(st.data);
  }, [tab]);

  useEffect(() => { load(); api.get("/meta/categories").then((r) => setCats(r.data)).catch(() => {}); }, [load]);

  const pull = async () => {
    setPulling(true); setPullResult("");
    try {
      const { data } = await api.post("/feeds/pull");
      setPullResult(`${data.feeds_processed} फ़ीड्स से ${data.routine_added} नई खबरें आईं · ${data.pr_published} PR ऑटो-प्रकाशित${data.errors ? ` · ${data.errors} फ़ीड में समस्या` : ""}`);
      await load();
    } catch (e) {
      setPullResult(apiErr(e.response?.data?.detail) || "फ़ीड लोड नहीं हो सकी");
    }
    finally { setPulling(false); }
  };

  const pullOne = async (id) => {
    setPullingFeed(id);
    try { await api.post(`/feeds/${id}/pull`); await load(); }
    finally { setPullingFeed(null); }
  };

  const removeFeed = async (id) => {
    if (!window.confirm("यह फ़ीड हटाएँ?")) return;
    await api.delete(`/feeds/${id}`);
    load();
  };

  const addFeed = async () => {
    setAddErr(""); setAddBusy(true);
    try {
      if (!addForm.name.trim() || !addForm.url.trim()) {
        setAddErr("नाम और URL ज़रूरी हैं"); setAddBusy(false); return;
      }
      await api.post("/feeds", addForm);
      setShowAdd(false);
      setAddForm({ name: "", url: "", type: "routine", mode: "review", category: "national" });
      load();
    } catch (e) {
      setAddErr(apiErr(e.response?.data?.detail) || "फ़ीड जोड़ नहीं सका");
    } finally { setAddBusy(false); }
  };

  const approve = async (id) => { await api.post(`/feed-items/${id}/approve`); load(); };
  const reject = async (id) => { await api.post(`/feed-items/${id}/reject`); load(); };
  const saveEdit = async () => {
    await api.put(`/feed-items/${edit.id}`, {
      title: edit.title, summary: edit.summary, body: edit.body, category: edit.category, kicker: edit.kicker,
    });
    setEdit(null); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-serif-dev">फ़ीड इनबॉक्स</h1>
          <p className="text-gray-500 text-sm mt-1">जनसंपर्क (PR) फ़ीड अपने-आप प्रकाशित; रूटीन फ़ीड यहाँ समीक्षा के बाद ही साइट पर जाएगी।</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <button onClick={() => setShowAdd(true)} data-testid="feed-add-btn"
              className="inline-flex items-center gap-2 border border-gray-300 text-gray-700 bg-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-50">
              <Plus size={16} /> नई फ़ीड जोड़ें
            </button>
          )}
          <button onClick={pull} data-testid="feed-pull-btn" disabled={pulling}
            className="inline-flex items-center gap-2 bg-[#0A0F1E] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#D92525] transition-colors disabled:opacity-60">
            {pulling ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} सभी फ़ीड्स खींचें
          </button>
        </div>
      </div>

      {pullResult && (
        <div data-testid="feed-pull-result" className="mb-4 bg-[#0A0F1E] text-white text-sm px-4 py-2.5 rounded-lg">
          {pullResult}
        </div>
      )}

      {/* feed sources + stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 mb-6">
        {feeds.map((f) => (
          <div key={f.id} data-testid={`feed-src-${f.id}`} className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 truncate">
                  {f.type === "pr" ? <Radio size={15} className="text-green-600 shrink-0" /> : <Rss size={15} className="text-amber-600 shrink-0" />}
                  <span className="truncate">{f.name}</span>
                </div>
                <div className="mt-2 flex items-center gap-2 text-[11px]">
                  <span className={`px-2 py-0.5 rounded-full font-medium ${f.mode === "auto" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
                    {f.mode === "auto" ? "ऑटो-प्रकाशित" : "समीक्षा"}
                  </span>
                  <span className="text-gray-400 truncate">{f.category}</span>
                </div>
                {f.url && (
                  <a href={f.url} target="_blank" rel="noreferrer" className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-gray-400 hover:text-[#D92525] truncate max-w-full">
                    <ExternalLink size={10} /> <span className="truncate">{f.url}</span>
                  </a>
                )}
                {f.last_error && (
                  <div className="mt-1.5 flex items-start gap-1 text-[11px] text-[#D92525]">
                    <AlertCircle size={11} className="mt-0.5 shrink-0" /> {f.last_error}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <button onClick={() => pullOne(f.id)} disabled={pullingFeed === f.id} data-testid={`feed-pull-${f.id}`}
                  className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-50" title="इस फ़ीड को खींचें">
                  {pullingFeed === f.id ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                </button>
                {isAdmin && (
                  <button onClick={() => removeFeed(f.id)} data-testid={`feed-del-${f.id}`}
                    className="p-1.5 rounded-md text-[#D92525] hover:bg-red-50" title="हटाएँ">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {stats && (
        <div className="flex gap-4 mb-6 text-sm flex-wrap">
          <span className="text-gray-500">समीक्षा में: <b className="text-amber-600">{stats.pending}</b></span>
          <span className="text-gray-500">स्वीकृत: <b className="text-green-600">{stats.approved}</b></span>
          <span className="text-gray-500">PR ऑटो-प्रकाशित: <b className="text-[#D92525]">{stats.pr_auto}</b></span>
          <span className="text-gray-500">रूटीन प्रकाशित: <b className="text-[#0A0F1E]">{stats.routine || 0}</b></span>
        </div>
      )}

      <div className="flex gap-2 mb-5">
        {["pending", "approved", "rejected"].map((tt) => (
          <button key={tt} data-testid={`feed-tab-${tt}`} onClick={() => setTab(tt)}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${tab === tt ? "bg-[#0A0F1E] text-white" : "bg-white text-gray-600 border border-gray-200"}`}>
            {tt === "pending" ? "समीक्षा में" : tt === "approved" ? "स्वीकृत" : "अस्वीकृत"}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {items.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-10 text-center text-gray-400">
            <Inbox className="mx-auto mb-3" /> कोई आइटम नहीं
          </div>
        )}
        {items.map((it) => (
          <div key={it.id} data-testid={`feed-item-${it.id}`} className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm flex gap-4">
            <img src={it.thumbnail} alt="" className="w-28 h-20 rounded-lg object-cover bg-gray-100 shrink-0" onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider text-gray-400 mb-1 truncate">{it.source} · {it.kicker} · {it.category}</div>
              <h3 className="font-serif-dev font-bold text-[18px] text-gray-900">{it.title}</h3>
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">{it.summary}</p>
              {it.status === "pending" && (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <button data-testid={`feed-approve-${it.id}`} onClick={() => approve(it.id)} className="inline-flex items-center gap-1.5 bg-green-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-green-700"><Check size={13} /> स्वीकृत कर प्रकाशित करें</button>
                  <button data-testid={`feed-edit-${it.id}`} onClick={() => { setEdit(it); }} className="inline-flex items-center gap-1.5 border border-gray-300 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-50"><Pencil size={13} /> एडिट</button>
                  <button data-testid={`feed-reject-${it.id}`} onClick={() => reject(it.id)} className="inline-flex items-center gap-1.5 text-[#D92525] px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-red-50"><X size={13} /> अस्वीकार</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add feed modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto" data-testid="feed-add-modal">
            <h2 className="text-lg font-bold mb-4">नई RSS/API फ़ीड जोड़ें</h2>
            {addErr && <div className="mb-3 text-sm text-[#D92525] bg-red-50 px-3 py-2 rounded">{addErr}</div>}
            <label className="block mb-3"><span className="text-[11px] uppercase tracking-wider text-gray-500">नाम</span>
              <input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} data-testid="feed-add-name"
                className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 focus:border-[#D92525] outline-none" placeholder="जैसे: जनसंपर्क विभाग MP" /></label>
            <label className="block mb-3"><span className="text-[11px] uppercase tracking-wider text-gray-500">RSS URL</span>
              <input value={addForm.url} onChange={(e) => setAddForm({ ...addForm, url: e.target.value })} data-testid="feed-add-url"
                className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 focus:border-[#D92525] outline-none" placeholder="https://.../rss.xml" /></label>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <label className="block"><span className="text-[11px] uppercase tracking-wider text-gray-500">प्रकार</span>
                <select value={addForm.type} onChange={(e) => setAddForm({ ...addForm, type: e.target.value, mode: e.target.value === "pr" ? "auto" : "review" })}
                  data-testid="feed-add-type" className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 bg-white">
                  <option value="pr">PR (जनसंपर्क)</option>
                  <option value="routine">रूटीन</option>
                </select></label>
              <label className="block"><span className="text-[11px] uppercase tracking-wider text-gray-500">मोड</span>
                <select value={addForm.mode} onChange={(e) => setAddForm({ ...addForm, mode: e.target.value })}
                  data-testid="feed-add-mode" className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 bg-white">
                  <option value="auto">ऑटो-प्रकाशित</option>
                  <option value="review">समीक्षा</option>
                </select></label>
            </div>
            <label className="block mb-4"><span className="text-[11px] uppercase tracking-wider text-gray-500">श्रेणी</span>
              <select value={addForm.category} onChange={(e) => setAddForm({ ...addForm, category: e.target.value })}
                data-testid="feed-add-category" className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 bg-white">
                {(cats.length ? cats.map((c) => c.key) : CATEGORY_KEYS).map((k) => {
                  const c = cats.find((x) => x.key === k);
                  return <option key={k} value={k}>{c ? c.label : k}</option>;
                })}
              </select></label>
            <div className="flex gap-2">
              <button onClick={addFeed} disabled={addBusy} data-testid="feed-add-save"
                className="flex-1 bg-[#D92525] text-white rounded-lg py-2.5 font-semibold hover:bg-[#0A0F1E] disabled:opacity-60 flex items-center justify-center gap-2">
                {addBusy && <Loader2 size={14} className="animate-spin" />} जोड़ें
              </button>
              <button onClick={() => setShowAdd(false)} className="px-5 border border-gray-300 rounded-lg font-semibold text-gray-600">रद्द</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit item modal */}
      {edit && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" data-testid="feed-edit-modal">
            <h2 className="text-lg font-bold mb-4">फ़ीड खबर संपादित करें</h2>
            <label className="block mb-3"><span className="text-[11px] uppercase tracking-wider text-gray-500">शीर्षक</span>
              <input data-testid="feed-edit-title" value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 focus:border-[#D92525] outline-none" /></label>
            <label className="block mb-3"><span className="text-[11px] uppercase tracking-wider text-gray-500">श्रेणी</span>
              <select value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 bg-white">
                {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select></label>
            <label className="block mb-3"><span className="text-[11px] uppercase tracking-wider text-gray-500">सारांश</span>
              <textarea data-testid="feed-edit-summary" value={edit.summary} onChange={(e) => setEdit({ ...edit, summary: e.target.value })} rows={2} className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 resize-none focus:border-[#D92525] outline-none" /></label>
            <label className="block mb-4"><span className="text-[11px] uppercase tracking-wider text-gray-500">खबर</span>
              <textarea data-testid="feed-edit-body" value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} rows={6} className="mt-1 w-full border border-gray-200 rounded-lg py-2 px-3 resize-y focus:border-[#D92525] outline-none font-serif-dev" /></label>
            <div className="flex gap-2">
              <button data-testid="feed-edit-save" onClick={saveEdit} className="flex-1 bg-[#D92525] text-white rounded-lg py-2.5 font-semibold hover:bg-[#0A0F1E]">सहेजें</button>
              <button onClick={() => setEdit(null)} className="px-5 border border-gray-300 rounded-lg font-semibold text-gray-600">रद्द</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
